import { _isBlank, _isNil, _isNotEmpty, _isNumber, _notNil, uuid } from '@/littledash';
import { type ISODate, Order } from '@/model/Common.model';
import type { Measurement } from '@/model/Measurement.model';
import type { Preset } from '@/model/Preset.model';
import { addDays, format } from 'date-fns';

export const orderMeasurementsByDate = (measurements: Array<Measurement> = [], order: Order = Order.asc) =>
  [...measurements].sort(
    (a, b) =>
      new Date((order === Order.asc ? a : b).measured_at).getTime() -
      new Date((order === Order.asc ? b : a).measured_at).getTime()
  );

export const findComparatorDateMeasurement = (
  measurements: Array<Measurement> = [],
  { comparatorDate, measurementKey }: { comparatorDate?: string; measurementKey: string }
): Measurement | null => {
  if (_notNil(comparatorDate)) {
    return (
      measurements.filter(
        (measurement) =>
          new Date(measurement.measured_at) >= new Date(comparatorDate) &&
          _notNil(measurement.variables?.[measurementKey])
      )?.[0] ?? null
    );
  }
  return null;
};

export const filterMeasurementsToDate = (
  measurements: Array<Measurement>,
  measuredAtDate?: ISODate
): Array<Measurement> => {
  if (_isNil(measuredAtDate) || _isBlank(measuredAtDate)) {
    return measurements;
  }
  const targetDate = new Date(measuredAtDate);
  return measurements.filter((measurement) => new Date(measurement.measured_at) < targetDate);
};

export const findFirstMeasurement = (measurements: Array<Measurement>, measurementKey: string): Measurement | null => {
  return measurements.find((measurement) => _notNil(measurement.variables?.[measurementKey])) ?? null;
};

export const findLatestMeasurement = (measurements: Array<Measurement>, measurementKey: string): Measurement | null => {
  for (let i = measurements.length - 1; i >= 0; i--) {
    if (_notNil(measurements[i].variables?.[measurementKey])) {
      return measurements[i];
    }
  }
  return null;
};

export const findMaximumMeasurement = (
  measurements: Array<Measurement>,
  measurementKey: string
): Measurement | null => {
  return measurements.reduce<Measurement | null>((maxMeasurement, current) => {
    const currentValue = current.variables?.[measurementKey];
    if (_isNil(currentValue)) {
      return maxMeasurement;
    }
    const maxValue = maxMeasurement?.variables?.[measurementKey];
    return _isNil(maxValue) || Number(currentValue) > Number(maxValue) ? current : maxMeasurement;
  }, null);
};

export const findDifference = (value: number | null, compareValue: number | null): number | null =>
  _notNil(compareValue) && _isNumber(Number(compareValue)) && _notNil(value) && _isNumber(value)
    ? value - compareValue
    : null;

export const presetFactory = (size: number): any => {
  return Array.from({ length: size }).map((_) => ({
    id: uuid(),
    name: uuid(),
    formula: uuid(),
    unit: uuid(),
    measurements: [
      {
        id: uuid(),
        name: uuid(),
        unit: uuid(),
      },
    ],
  }));
};

export const measurementFactory = (size: number, calculations?: Preset['calculations']): Array<Measurement> => {
  return Array.from({ length: size }).map((_, index) => ({
    id: index + 1,
    api_id: `mst_${uuid().replaceAll('-', '')}`,
    measured_at: format(addDays(new Date('1991-01-01'), index), 'yyyy-MM-dd'),
    variables: (calculations ?? [])?.reduce<Measurement['variables']>((acc, { id, measurements }) => {
      acc = {
        ...acc,
        [id]: Math.abs(Math.random() * (0 - 150)),
      };
      if (_isNotEmpty(measurements)) {
        acc = {
          ...acc,
          ...measurements.reduce((measurementAcc, { id: measurementId }) => {
            measurementAcc = {
              ...measurementAcc,
              [measurementId]: Math.abs(Math.random() * (0 - 9999)),
            };
            return measurementAcc;
          }, {}),
        };
      }
      return acc;
    }, {}),
    created_at: '',
  }));
};
